import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, withKeys as _withKeys, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createBlock as _createBlock, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "timeline-toolbar" }
const _hoisted_2 = { class: "timeline-actions" }
const _hoisted_3 = {
  class: "display-duration-control",
  title: "设置可见宽度内的时长；清空后自动计算。超出部分可横向滚动查看。"
}
const _hoisted_4 = ["placeholder", "value"]
const _hoisted_5 = ["onKeydown"]
const _hoisted_6 = { class: "add-line-options" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "timeline-warnings"
}
const _hoisted_10 = { class: "timeline-body" }
const _hoisted_11 = { class: "timeline-ruler" }
const _hoisted_12 = { class: "line-label dialogue-label" }
const _hoisted_13 = { class: "line-buttons" }
const _hoisted_14 = ["disabled", "title"]
const _hoisted_15 = ["onPointerdown", "onContextmenu", "onPointerenter", "onClick"]
const _hoisted_16 = ["onPointerdown"]
const _hoisted_17 = ["onPointerdown"]
const _hoisted_18 = ["onPointerdown"]
const _hoisted_19 = ["onContextmenu"]
const _hoisted_20 = { class: "line-label" }
const _hoisted_21 = { class: "line-buttons" }
const _hoisted_22 = ["aria-label", "onClick"]
const _hoisted_23 = ["aria-label", "onClick"]
const _hoisted_24 = ["onPointerdown", "onContextmenu", "onPointerenter", "onClick"]
const _hoisted_25 = ["onPointerdown"]
const _hoisted_26 = ["title"]
const _hoisted_27 = ["onPointerdown"]
const _hoisted_28 = ["onClick"]
const _hoisted_29 = { class: "timeline-add-event" }
const _hoisted_30 = ["aria-expanded"]
const _hoisted_31 = { key: 0 }
const _hoisted_32 = { key: 1 }
const _hoisted_33 = { key: 2 }
const _hoisted_34 = { key: 3 }
const _hoisted_35 = { class: "popover-header" }
const _hoisted_36 = {
  key: 4,
  class: "number-fields"
}
const _hoisted_37 = ["value"]
const _hoisted_38 = { key: 0 }
const _hoisted_39 = ["value", "min"]
const _hoisted_40 = { key: 1 }
const _hoisted_41 = ["value"]
const _hoisted_42 = { key: 2 }
const _hoisted_43 = ["min", "value"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type {
  DialogueClip,
  FocusPushClip,
  DialogueNode,
  PerformanceClip,
  PerformanceLine,
  PerformanceLineType,
  SelectClip,
} from "./types/DialogueNode";
import {
  createDialogueClip,
  createFocusPushClip,
  createPerformanceClip,
  createPerformanceLine,
  createSelectClip,
} from "./utils/dialogueProject";
import {
  getLineDefinition,
  getLineDefinitions,
} from "./config/lineRegistry";
import DialogueClipEditor from "./components/clip-editors/DialogueClipEditor.vue";
import FocusPushClipEditor from "./components/clip-editors/FocusPushClipEditor.vue";
import SelectClipEditor from "./components/clip-editors/SelectClipEditor.vue";
import PerformanceClipEditor from "./components/clip-editors/PerformanceClipEditor.vue";
import TimelineContextMenu from "./components/TimelineContextMenu.vue";
import {
  captureTimelineClip,
  findTimelineClip,
  pasteTimelineClip,
  timelineClipClipboard,
  timelinePasteHint,
  type TimelineLane,
} from "./utils/timelineClipClipboard";
import { usePublicEventPresets } from "../EntityPresetEditor/usePublicEventPresets";
import { getPublicEventClipLabel } from "./utils/publicEventParameters";
import { getCameraClipPreview } from "./config/cameraClip";
import { getGroupOutletWarnings } from "./utils/groupOutlets";
import { getDialogueClips, appendDialogueClip, removeDialogueClip } from "./utils/dialogueClips";
import {
  getFlowClipDuration,
  getGroupTimelineEnd,
  getGroupTimelineDisplayDuration,
  isPerformanceClipResizable,
  getPerformanceClipDuration,
  isFreeDialogueClip,
  MIN_CLIP_DURATION,
  type FlowClip,
} from "./utils/groupTimeline";

type SelectedClip =
  | { kind: "focusPush"; clip: FocusPushClip }
  | { kind: "dialogue"; clip: DialogueClip }
  | { kind: "select"; clip: SelectClip }
  | { kind: "performance"; line: PerformanceLine; clip: PerformanceClip };

const labelWidth = 118;
type TimelineClip = DialogueClip | SelectClip | PerformanceClip | FocusPushClip;
type TimelineGesture = {
  pointerId: number;
  pointerStart: number;
  scrollStart: number;
  pixelsPerSecond: number;
  moved: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'GroupTimelineV3',
  props: {
    node: {},
    inspectorTarget: {}
  },
  emits: ["close", "inspectorOpen"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const { availablePresets: publicEventPresets } = usePublicEventPresets();

const viewportWidth = ref(918);
const timelineScrollRef = ref<HTMLElement>();
let timelineResizeObserver: ResizeObserver | undefined;
const dialogueClips = computed(() => getDialogueClips(props.node));
const canAddDialogueClip = computed(() => dialogueClips.value.every(clip => clip.advanceMode === "None"));
const selectedId = ref(dialogueClips.value[0]?.id ?? "");
const addLineMenu = ref<HTMLDetailsElement>();
const footerLineMenu = ref<{ x: number; y: number; trigger: HTMLElement }>();
const sectionRef = ref<HTMLElement>();
const editorOpen = ref(false);
let outsideEditorCloseTimer: number | undefined;
let editorPointerDownInside = false;
const hoveredClip = ref<SelectedClip>();
const previewPosition = ref({ left: 0, top: 0 });
const timelineContextMenu = ref<{
  nodeId: string;
  lane: TimelineLane;
  clipId?: string;
  time: number;
  x: number;
  y: number;
  trigger: HTMLElement;
}>();
const contextClip = computed(() => timelineContextMenu.value?.clipId
  ? findTimelineClip(props.node, timelineContextMenu.value.clipId) : undefined);
const contextTrackAvailable = computed(() => {
  const lane = timelineContextMenu.value?.lane;
  return !!lane && (lane.kind === "dialogue" ? canAddDialogueClip.value : lane.kind === "performance"
    ? props.node.lines.some(line => line.id === lane.lineId) : !props.node[lane.kind]);
});
const contextPasteHint = computed(() => timelineContextMenu.value
  ? timelinePasteHint(props.node, timelineContextMenu.value.lane, timelineClipClipboard.value, timelineContextMenu.value.time) : "");
const contextMenuItems = computed(() => timelineContextMenu.value?.clipId ? [
  { id: "copy", label: "复制 Clip", disabled: !contextClip.value },
  { id: "delete", label: "删除 Clip", danger: true, disabled: !contextClip.value },
] : [
  { id: "add", label: "添加 Clip", disabled: !contextTrackAvailable.value },
  { id: "paste", label: "粘贴 Clip", disabled: !!contextPasteHint.value, hint: contextPasteHint.value },
]);
const lineDefinitions = getLineDefinitions().filter(
  (definition) => definition.removable && definition.type !== "Audio",
).sort((a, b) => {
  const priority = (type: PerformanceLineType) => type === "PublicEvent" ? 0 : type === "Custom" ? 1 : 2;
  return priority(a.type) - priority(b.type);
});

const outletWarnings = computed(() =>
  getGroupOutletWarnings(props.node),
);

const selectedClip = computed<SelectedClip | undefined>(() => {
  const dialogue = dialogueClips.value.find(clip => clip.id === selectedId.value);
  if (dialogue) {
    return { kind: "dialogue", clip: dialogue };
  }
  if (props.node.select && selectedId.value === props.node.select.id) {
    return { kind: "select", clip: props.node.select };
  }

  if (props.node.focusPush && selectedId.value === props.node.focusPush.id) {
    return { kind: "focusPush", clip: props.node.focusPush };
  }

  for (const line of props.node.lines) {
    const clip = line.clips.find((item) => item.id === selectedId.value);
    if (clip) return { kind: "performance", line, clip };
  }
  return undefined;
});

watch(() => editorOpen.value && Boolean(selectedClip.value), open => emit("inspectorOpen", open), { immediate: true, flush: "sync" });

const contentDuration = computed(() => getGroupTimelineEnd(props.node));
// Auto-fit must not change the coordinate system while a pointer gesture is active.
const gestureViewport = ref<{ displayDuration: number; pixelsPerSecond: number; canvasDuration: number }>();
const timelineDuration = computed(() =>
  gestureViewport.value?.displayDuration ?? getGroupTimelineDisplayDuration(props.node),
);
const pixelsPerSecond = computed(() => gestureViewport.value?.pixelsPerSecond
  ?? Math.max(1, viewportWidth.value - labelWidth - 24) / timelineDuration.value);
const canvasDuration = computed(() => Math.max(timelineDuration.value, contentDuration.value, gestureViewport.value?.canvasDuration ?? 0));
watch(contentDuration, end => {
  // Keep scrollLeft from being clamped when a dragged Clip shortens the content.
  if (gestureViewport.value) gestureViewport.value.canvasDuration = Math.max(gestureViewport.value.canvasDuration, end);
}, { flush: "sync" });
const timelineWidth = computed(
  () => labelWidth + canvasDuration.value * pixelsPerSecond.value + 24,
);
const tickStep = computed(() => {
  const target = Math.max(70 / pixelsPerSecond.value, canvasDuration.value / 2000);
  const magnitude = 10 ** Math.floor(Math.log10(target));
  return ([1, 2, 5, 10].find(value => value * magnitude >= target) ?? 10) * magnitude;
});
const ticks = computed(() => {
  const step = tickStep.value;
  return Array.from({ length: Math.floor(canvasDuration.value / step) + 1 }, (_, index) => Number((index * step).toPrecision(10)));
});
function updateDisplayDuration(event: Event) {
  const input = event.target as HTMLInputElement;
  const value = input.valueAsNumber;
  if (input.value === "") delete props.node.timeline.displayDuration;
  else if (Number.isFinite(value) && value > 0) props.node.timeline.displayDuration = value;
  input.value = props.node.timeline.displayDuration?.toString() ?? "";
  if (timelineScrollRef.value) timelineScrollRef.value.scrollLeft = 0;
}

watch(
  () => props.node,
  () => {
    stopTimelineGesture();
    cancelOutsideEditorClose();
    resetEditorPointerDown();
    closeFooterLineMenu();
    closeTimelineContextMenu();
    selectedId.value = dialogueClips.value[0]?.id ?? "";
    editorOpen.value = false;
    hoveredClip.value = undefined;
    addLineMenu.value?.removeAttribute("open");
  },
  { flush: "sync" },
);

function closeLineMenu() {
  const footerTrigger = footerLineMenu.value?.trigger;
  closeFooterLineMenu();
  addLineMenu.value?.removeAttribute("open");
  (footerTrigger ?? addLineMenu.value?.querySelector("summary"))?.focus({ preventScroll: true });
}

function closeFooterLineMenu(restoreFocus = false) {
  const trigger = footerLineMenu.value?.trigger;
  footerLineMenu.value = undefined;
  if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
}

function openFooterLineMenu(event: MouseEvent) {
  closeLineMenu();
  closeTimelineContextMenu();
  const trigger = event.currentTarget as HTMLElement;
  const rect = trigger.getBoundingClientRect();
  footerLineMenu.value = { x: rect.left, y: rect.bottom + 6, trigger };
}

function closeLineMenuOutside(event: PointerEvent) {
  const menu = addLineMenu.value;
  if (menu?.open && !event.composedPath().includes(menu)) menu.open = false;
}

function addLine(type: PerformanceLineType) {
  if (!lineDefinitions.some(definition => definition.type === type)) return;
  props.node.lines.push(createPerformanceLine(type));
  closeLineMenu();
}

function addDialogueClip(startTime?: number) {
  if (!canAddDialogueClip.value) return;
  const previousEnd = dialogueClips.value.reduce((end, clip) => Math.max(end, clip.startTime + getFlowClipDuration(props.node, clip)), 0);
  const clip = createDialogueClip();
  clip.startTime = Math.max(previousEnd, Number.isFinite(startTime) ? startTime! : 0);
  appendDialogueClip(props.node, clip);
  selectedId.value = clip.id;
}

function addSelectClip() {
  if (props.node.select) return;
  const clip = createSelectClip();
  props.node.select = clip;
  selectedId.value = clip.id;
}

function addFocusPushClip() {
  if (props.node.focusPush) return;
  const clip = createFocusPushClip();
  props.node.focusPush = clip;
  selectedId.value = clip.id;
}

function deleteLine(line: PerformanceLine) {
  if (getLineDefinition(line.type)?.removable === false) return;
  const index = props.node.lines.findIndex((item) => item.id === line.id);
  if (index < 0) return;
  if (line.clips.some((clip) => clip.id === selectedId.value)) {
    selectedId.value = "";
    editorOpen.value = false;
  }
  props.node.lines.splice(index, 1);
}

function performanceClipLabel(clip: PerformanceClip) {
  if (clip.type === "PublicEvent") return getPublicEventClipLabel(clip, publicEventPresets.value);
  if (clip.type !== "Custom") return clip.name;
  const templateId = "custom.data";
  const value = clip.components.find(component => component.templateId === templateId)?.properties.value;
  return typeof value === "string" && value ? value : "未填写触发字符串";
}

function lineLabel(line: PerformanceLine) {
  return getLineDefinition(line.type)?.label ?? line.type;
}

function addPerformanceClip(line: PerformanceLine) {
  const startTime = line.clips.reduce(
    (maximum, clip) => Math.max(maximum, clip.startTime + (isPerformanceClipResizable(clip) ? getPerformanceClipDuration(clip) : 1)),
    0,
  );
  const clip = createPerformanceClip(line.type, startTime);
  line.clips.push(clip);
  selectedId.value = clip.id;
}

function deleteSelectedClip() {
  const selected = selectedClip.value;
  if (!selected) return;
  if (selected.kind === "dialogue") {
    removeDialogueClip(props.node, selected.clip.id);
  } else if (selected.kind === "select") {
    props.node.select = undefined;
  } else if (selected.kind === "focusPush") {
    props.node.focusPush = undefined;
  } else {
    const index = selected.line.clips.findIndex(
      (clip) => clip.id === selected.clip.id,
    );
    if (index >= 0) selected.line.clips.splice(index, 1);
  }
  selectedId.value = "";
  editorOpen.value = false;
  hoveredClip.value = undefined;
}

function closeTimelineContextMenu(restoreFocus = false) {
  const trigger = timelineContextMenu.value?.trigger;
  timelineContextMenu.value = undefined;
  if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
}

function openTrackContextMenu(event: MouseEvent, lane: TimelineLane) {
  const row = event.currentTarget as HTMLElement;
  const onLabel = !!(event.target as Element).closest(".line-label");
  const time = onLabel ? (lane.kind === "focusPush" ? 1 : 0)
    : Math.max(0, Math.round((event.clientX - row.getBoundingClientRect().left - labelWidth) / pixelsPerSecond.value * 10) / 10);
  hoveredClip.value = undefined;
  closeLineMenu();
  timelineContextMenu.value = { nodeId: props.node.id, lane, time, x: event.clientX, y: event.clientY, trigger: row };
}

function openClipContextMenu(event: MouseEvent, selected: SelectedClip) {
  selectedId.value = selected.clip.id;
  hoveredClip.value = undefined;
  closeLineMenu();
  const lane: TimelineLane = selected.kind === "performance"
    ? { kind: "performance", lineId: selected.line.id } : { kind: selected.kind };
  timelineContextMenu.value = {
    nodeId: props.node.id, lane, clipId: selected.clip.id, time: selected.clip.startTime,
    x: event.clientX, y: event.clientY, trigger: event.currentTarget as HTMLElement,
  };
}

function timelineContextAction(action: string) {
  const target = timelineContextMenu.value;
  if (!target || target.nodeId !== props.node.id) {
    closeTimelineContextMenu();
    return;
  }
  if (target.clipId) {
    const selected = findTimelineClip(props.node, target.clipId);
    if (selected && action === "copy") timelineClipClipboard.value = captureTimelineClip(selected);
    if (selected && action === "delete") {
      selectedId.value = selected.clip.id;
      deleteSelectedClip();
    }
  } else if (action === "paste" && timelineClipClipboard.value) {
    const selected = pasteTimelineClip(props.node, target.lane, timelineClipClipboard.value, target.time);
    if (selected) selectedId.value = selected.clip.id;
  } else if (action === "add" && contextTrackAvailable.value) {
    if (target.lane.kind === "dialogue") addDialogueClip(target.time);
    else if (target.lane.kind === "select") addSelectClip();
    else if (target.lane.kind === "focusPush") addFocusPushClip();
    else {
      const lineId = target.lane.lineId;
      const line = props.node.lines.find(line => line.id === lineId);
      if (line) addPerformanceClip(line);
    }
    if (target.lane.kind !== "dialogue" && selectedClip.value) selectedClip.value.clip.startTime = target.time;
  }
  closeTimelineContextMenu(true);
}

function showPreview(event: PointerEvent, selected: SelectedClip) {
  if (gestureViewport.value) return;
  if (timelineContextMenu.value) return;
  if (editorOpen.value && selectedId.value === selected.clip.id) return;
  hoveredClip.value = selected;
  movePreview(event);
}

function movePreview(event: PointerEvent) {
  const section = sectionRef.value;
  if (!section || !hoveredClip.value) return;
  const rect = section.getBoundingClientRect();
  previewPosition.value = {
    left: Math.min(Math.max(8, event.clientX - rect.left + 12), rect.width - 230),
    top: Math.max(8, event.clientY - rect.top - 86),
  };
}

function hidePreview() {
  hoveredClip.value = undefined;
}

function cancelOutsideEditorClose() {
  if (outsideEditorCloseTimer !== undefined) window.clearTimeout(outsideEditorCloseTimer);
  outsideEditorCloseTimer = undefined;
}

function isEditorInteraction(event: Event) {
  const path = event.composedPath();
  return path.includes(props.inspectorTarget!) || path.some(target =>
    (target as Element).matches?.("[data-clip-editor], [data-timeline-clip]"));
}

function trackEditorPointerDown(event: PointerEvent) {
  editorPointerDownInside = event.button === 0 && isEditorInteraction(event);
}

function resetEditorPointerDown() {
  editorPointerDownInside = false;
}

function closeEditorFromOutside(event: MouseEvent) {
  // Dragging a text selection outside can send the click to a shared ancestor.
  const startedInside = event.type === "click" && event.detail > 0 && editorPointerDownInside;
  if (event.type === "click") resetEditorPointerDown();
  if (!editorOpen.value || startedInside || isEditorInteraction(event)) return;
  cancelOutsideEditorClose();
  // Finish the click first, so the graph's pane handler keeps the Timeline open.
  outsideEditorCloseTimer = window.setTimeout(() => {
    outsideEditorCloseTimer = undefined;
    editorOpen.value = false;
  }, 0);
}

function openEditor(_event: MouseEvent, selected: SelectedClip) {
  if (suppressClick) return;
  cancelOutsideEditorClose();
  selectedId.value = selected.clip.id;
  hoveredClip.value = undefined;
  editorOpen.value = true;
}

function updateStartTime(clip: TimelineClip, event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  if (!Number.isFinite(value)) return;
  clip.startTime = Math.max(0, value);
}

function updatePerformanceDuration(clip: PerformanceClip, event: Event) {
  if (!isPerformanceClipResizable(clip)) return;
  const value = Number((event.target as HTMLInputElement).value);
  if (!Number.isFinite(value)) return;
  clip.duration = Math.max(clip.type === "PublicEvent" ? 0 : MIN_CLIP_DURATION, value);
}

function updateDialogueDuration(clip: DialogueClip, event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  if (!isFreeDialogueClip(clip) || !Number.isFinite(value)) return;
  clip.duration = Math.max(MIN_CLIP_DURATION, value);
}

function updateContinueDelay(clip: FlowClip, event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  if (!Number.isFinite(value)) return;
  clip.continueDelayTime = Math.max(0, value);
}

function beginTimelineGesture(event: PointerEvent): TimelineGesture {
  stopTimelineGesture();
  gestureViewport.value = {
    displayDuration: timelineDuration.value,
    pixelsPerSecond: pixelsPerSecond.value,
    canvasDuration: canvasDuration.value,
  };
  hoveredClip.value = undefined;
  return {
    pointerId: event.pointerId,
    pointerStart: event.clientX,
    scrollStart: timelineScrollRef.value?.scrollLeft ?? 0,
    pixelsPerSecond: pixelsPerSecond.value,
    moved: false,
  };
}

function isTimelineGesturePointer(event: Event | undefined, gesture: TimelineGesture) {
  return !event || !("pointerId" in event) || (event as PointerEvent).pointerId === gesture.pointerId;
}

function timelineGestureDelta(event: PointerEvent, gesture: TimelineGesture) {
  return event.clientX - gesture.pointerStart + (timelineScrollRef.value?.scrollLeft ?? 0) - gesture.scrollStart;
}

function listenTimelineGesture(move: (event: PointerEvent) => void, stop: (event?: Event) => void) {
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", stop);
  window.addEventListener("pointercancel", stop);
  window.addEventListener("blur", stop);
}

function finishTimelineGesture(move: (event: PointerEvent) => void, stop: (event?: Event) => void, moved: boolean) {
  window.removeEventListener("pointermove", move);
  window.removeEventListener("pointerup", stop);
  window.removeEventListener("pointercancel", stop);
  window.removeEventListener("blur", stop);
  gestureViewport.value = undefined;
  suppressClick = moved;
  window.setTimeout(() => { suppressClick = false; });
}

function stopTimelineGesture() {
  stopDrag();
  stopResize();
  stopContinueDelayDrag();
}

let dragging:
  | (TimelineGesture & {
      clip: TimelineClip;
      clipStart: number;
    })
  | undefined;

function startDrag(
  event: PointerEvent,
  clip: TimelineClip,
) {
  if (event.button !== 0) return;
  event.preventDefault();
  selectedId.value = clip.id;
  dragging = {
    ...beginTimelineGesture(event),
    clip,
    clipStart: clip.startTime,
  };
  listenTimelineGesture(dragClip, stopDrag);
}

function dragClip(event: PointerEvent) {
  if (!dragging || !isTimelineGesturePointer(event, dragging)) return;
  const pixelDelta = timelineGestureDelta(event, dragging);
  const delta = pixelDelta / dragging.pixelsPerSecond;
  if (Math.abs(pixelDelta) > 3) {
    dragging.moved = true;
  }
  dragging.clip.startTime = Math.max(
    0,
    Math.round((dragging.clipStart + delta) * 10) / 10,
  );
}

function stopDrag(event?: Event) {
  if (!dragging || !isTimelineGesturePointer(event, dragging)) return;
  const moved = dragging.moved;
  dragging = undefined;
  finishTimelineGesture(dragClip, stopDrag, moved);
}

let resizing:
  | (TimelineGesture & {
      clip: TimelineClip;
      boundary: "start" | "end";
      clipStart: number;
      clipDuration: number;
    })
  | undefined;

function startResize(
  event: PointerEvent,
  clip: TimelineClip,
  boundary: "start" | "end",
) {
  if (event.button !== 0 || ("type" in clip && !isPerformanceClipResizable(clip))) return;
  if (boundary === "end" && !("type" in clip) && !isFreeDialogueClip(clip)) return;
  event.preventDefault();
  event.stopPropagation();
  selectedId.value = clip.id;
  resizing = {
    ...beginTimelineGesture(event),
    clip,
    boundary,
    clipStart: clip.startTime,
    clipDuration: clipDisplayDuration(clip),
  };
  listenTimelineGesture(resizeClip, stopResize);
}

function resizeClip(event: PointerEvent) {
  if (!resizing || !isTimelineGesturePointer(event, resizing)) return;
  if ("type" in resizing.clip && !isPerformanceClipResizable(resizing.clip)) {
    stopResize(event);
    return;
  }
  const pixelDelta = timelineGestureDelta(event, resizing);
  const timeDelta = pixelDelta / resizing.pixelsPerSecond;
  if (Math.abs(pixelDelta) > 3) resizing.moved = true;

  if (resizing.boundary === "start") {
    const originalEnd = resizing.clipStart + resizing.clipDuration;
    const nextStart = Math.min(
      originalEnd - 0.1,
      Math.max(0, Math.round((resizing.clipStart + timeDelta) * 10) / 10),
    );
    resizing.clip.startTime = nextStart;
    if ("type" in resizing.clip || isFreeDialogueClip(resizing.clip)) {
      resizing.clip.duration = Math.max(
        MIN_CLIP_DURATION,
        Math.round((originalEnd - nextStart) * 10) / 10,
      );
    }
    return;
  }

  if ("type" in resizing.clip || isFreeDialogueClip(resizing.clip)) {
    resizing.clip.duration = Math.max(
      MIN_CLIP_DURATION,
      Math.round((resizing.clipDuration + timeDelta) * 10) / 10,
    );
  }
}

function stopResize(event?: Event) {
  if (!resizing || !isTimelineGesturePointer(event, resizing)) return;
  const moved = resizing.moved;
  resizing = undefined;
  finishTimelineGesture(resizeClip, stopResize, moved);
}

function clipDisplayDuration(clip: TimelineClip) {
  return "type" in clip
    ? getPerformanceClipDuration(clip)
    : "continueDelayTime" in clip ? getFlowClipDuration(props.node, clip) : 0;
}

let continueDelayDragging:
  | (TimelineGesture & {
      clip: FlowClip;
      delayStart: number;
    })
  | undefined;

function startContinueDelayDrag(event: PointerEvent, clip: FlowClip) {
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();
  selectedId.value = clip.id;
  continueDelayDragging = {
    ...beginTimelineGesture(event),
    clip,
    delayStart: clip.continueDelayTime,
  };
  listenTimelineGesture(dragContinueDelay, stopContinueDelayDrag);
}

function dragContinueDelay(event: PointerEvent) {
  if (!continueDelayDragging || !isTimelineGesturePointer(event, continueDelayDragging)) return;
  const pixelDelta = timelineGestureDelta(event, continueDelayDragging);
  if (Math.abs(pixelDelta) > 3) continueDelayDragging.moved = true;
  continueDelayDragging.clip.continueDelayTime = Math.max(
    0,
    Math.round(
      (continueDelayDragging.delayStart + pixelDelta / continueDelayDragging.pixelsPerSecond) * 10,
    ) / 10,
  );
}

function stopContinueDelayDrag(event?: Event) {
  if (!continueDelayDragging || !isTimelineGesturePointer(event, continueDelayDragging)) return;
  const moved = continueDelayDragging.moved;
  continueDelayDragging = undefined;
  finishTimelineGesture(dragContinueDelay, stopContinueDelayDrag, moved);
}

let suppressClick = false;

onMounted(() => {
  window.addEventListener("click", closeEditorFromOutside, true);
  window.addEventListener("contextmenu", closeEditorFromOutside, true);
  window.addEventListener("pointerdown", trackEditorPointerDown, true);
  window.addEventListener("pointercancel", resetEditorPointerDown, true);
  window.addEventListener("blur", resetEditorPointerDown);
  window.addEventListener("pointerdown", closeLineMenuOutside, true);
  timelineResizeObserver = new ResizeObserver(() => {
    if (timelineScrollRef.value) viewportWidth.value = timelineScrollRef.value.clientWidth;
  });
  if (timelineScrollRef.value) {
    viewportWidth.value = timelineScrollRef.value.clientWidth;
    timelineResizeObserver.observe(timelineScrollRef.value);
  }
});
onBeforeUnmount(() => {
  closeFooterLineMenu();
  cancelOutsideEditorClose();
  window.removeEventListener("click", closeEditorFromOutside, true);
  window.removeEventListener("contextmenu", closeEditorFromOutside, true);
  window.removeEventListener("pointerdown", trackEditorPointerDown, true);
  window.removeEventListener("pointercancel", resetEditorPointerDown, true);
  window.removeEventListener("blur", resetEditorPointerDown);
  closeTimelineContextMenu();
  window.removeEventListener("pointerdown", closeLineMenuOutside, true);
  emit("inspectorOpen", false);
  timelineResizeObserver?.disconnect();
  stopTimelineGesture();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    ref_key: "sectionRef",
    ref: sectionRef,
    class: "group-timeline-v3"
  }, [
    _createElementVNode("header", _hoisted_1, [
      _cache[23] || (_cache[23] = _createElementVNode("span", { class: "timeline-eyebrow" }, "GROUP TIMELINE", -1)),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("label", _hoisted_3, [
          _cache[21] || (_cache[21] = _createTextVNode(" 显示时长（秒） ", -1)),
          _createElementVNode("input", {
            type: "number",
            min: "0.1",
            step: "0.1",
            "aria-label": "Timeline 显示时长（秒）",
            placeholder: `自动（${timelineDuration.value}）`,
            value: __props.node.timeline.displayDuration ?? '',
            onChange: updateDisplayDuration
          }, null, 40, _hoisted_4)
        ]),
        _createElementVNode("details", {
          ref_key: "addLineMenu",
          ref: addLineMenu,
          class: "add-line-menu",
          onKeydown: _withKeys(_withModifiers(closeLineMenu, ["prevent","stop"]), ["esc"])
        }, [
          _cache[22] || (_cache[22] = _createElementVNode("summary", {
            class: "primary-button",
            "aria-label": "添加事件行"
          }, "＋ 添加事件", -1)),
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(lineDefinitions), (definition) => {
              return (_openBlock(), _createElementBlock("button", {
                key: definition.type,
                type: "button",
                onClick: ($event: any) => (addLine(definition.type))
              }, "添加" + _toDisplayString(definition.label), 9, _hoisted_7))
            }), 128))
          ])
        ], 40, _hoisted_5),
        _createElementVNode("button", {
          type: "button",
          disabled: !selectedClip.value,
          onClick: deleteSelectedClip
        }, " 删除 Clip ", 8, _hoisted_8),
        _createElementVNode("button", {
          type: "button",
          "aria-label": "关闭 Timeline",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
        }, " × ")
      ])
    ]),
    (outletWarnings.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(outletWarnings.value, (warning) => {
            return (_openBlock(), _createElementBlock("span", {
              key: warning,
              class: "timeline-warning"
            }, " ⚠ " + _toDisplayString(warning), 1))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", {
        ref_key: "timelineScrollRef",
        ref: timelineScrollRef,
        class: "timeline-scroll"
      }, [
        _createElementVNode("div", {
          class: "timeline-canvas",
          style: _normalizeStyle({ width: `${timelineWidth.value}px`, backgroundSize: `${tickStep.value * pixelsPerSecond.value}px 100%` })
        }, [
          _createElementVNode("div", _hoisted_11, [
            _cache[24] || (_cache[24] = _createElementVNode("div", { class: "ruler-corner" }, "LINE / TIME", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ticks.value, (tick) => {
              return (_openBlock(), _createElementBlock("span", {
                key: tick,
                class: "timeline-tick",
                style: _normalizeStyle({ left: `${labelWidth + tick * pixelsPerSecond.value}px` })
              }, _toDisplayString(tick) + "s ", 5))
            }), 128))
          ]),
          _createElementVNode("div", {
            class: "timeline-row dialogue-row",
            onContextmenu: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (openTrackContextMenu($event, { kind: 'dialogue' })), ["prevent","stop"]))
          }, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", null, [
                _cache[25] || (_cache[25] = _createElementVNode("strong", null, "对话", -1)),
                _createElementVNode("small", null, _toDisplayString(dialogueClips.value.length) + " 段台词", 1)
              ]),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("button", {
                  type: "button",
                  "aria-label": "添加对话 Clip",
                  disabled: !canAddDialogueClip.value,
                  title: canAddDialogueClip.value ? '在末尾添加对话片段' : '将末尾对话设为不触发按下后，可继续添加',
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (addDialogueClip()))
                }, "＋", 8, _hoisted_14)
              ])
            ]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dialogueClips.value, (clip) => {
              return (_openBlock(), _createElementBlock("button", {
                key: clip.id,
                type: "button",
                "data-timeline-clip": "",
                class: _normalizeClass(["timeline-clip dialogue-clip resizable-clip", { selected: selectedId.value === clip.id, 'waiting-dialogue-clip': !_unref(isFreeDialogueClip)(clip) }]),
                style: _normalizeStyle({
                left: `${labelWidth + clip.startTime * pixelsPerSecond.value}px`,
                width: _unref(isFreeDialogueClip)(clip) ? `${_unref(getFlowClipDuration)(__props.node, clip) * pixelsPerSecond.value}px` : undefined,
              }),
                onPointerdown: ($event: any) => (startDrag($event, clip)),
                onContextmenu: _withModifiers(($event: any) => (openClipContextMenu($event, { kind: 'dialogue', clip })), ["prevent","stop"]),
                onPointerenter: ($event: any) => (
                showPreview($event, { kind: 'dialogue', clip })
              ),
                onPointermove: movePreview,
                onPointerleave: hidePreview,
                onClick: _withModifiers(($event: any) => (
                openEditor($event, { kind: 'dialogue', clip })
              ), ["stop"])
              }, [
                _createElementVNode("i", {
                  class: "clip-resize-handle resize-start",
                  title: "拖动设置开始时间",
                  onPointerdown: _withModifiers(($event: any) => (startResize($event, clip, 'start')), ["stop"])
                }, null, 40, _hoisted_16),
                _createElementVNode("span", null, _toDisplayString(clip.content || "未填写台词"), 1),
                _createElementVNode("small", null, [
                  _createTextVNode(_toDisplayString(clip.startTime.toFixed(1)) + "s ", 1),
                  (_unref(isFreeDialogueClip)(clip))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(" + " + _toDisplayString(_unref(getFlowClipDuration)(__props.node, clip).toFixed(1)) + "s", 1)
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(" → 时间轴末尾")
                      ], 64))
                ]),
                (_unref(isFreeDialogueClip)(clip))
                  ? (_openBlock(), _createElementBlock("i", {
                      key: 0,
                      class: "clip-resize-handle resize-end",
                      title: "拖动设置持续时间",
                      onPointerdown: _withModifiers(($event: any) => (startResize($event, clip, 'end')), ["stop"])
                    }, null, 40, _hoisted_17))
                  : _createCommentVNode("", true),
                (clip.advanceMode === 'PlayerInput')
                  ? (_openBlock(), _createElementBlock("i", {
                      key: 1,
                      class: "continue-delay-handle",
                      style: _normalizeStyle({
                  left: `${clip.continueDelayTime * pixelsPerSecond.value}px`,
                }),
                      title: "拖动设置 ContinueDelayTime",
                      onPointerdown: _withModifiers(($event: any) => (startContinueDelayDrag($event, clip)), ["stop"])
                    }, [
                      _createElementVNode("em", null, _toDisplayString(clip.continueDelayTime.toFixed(1)) + "s", 1)
                    ], 44, _hoisted_18))
                  : _createCommentVNode("", true)
              ], 46, _hoisted_15))
            }), 128)),
            (!dialogueClips.value.length)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "empty-line",
                  style: _normalizeStyle({ left: `${labelWidth + 16}px` }),
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (addDialogueClip()))
                }, " ＋ 添加 对话片段 ", 4))
              : _createCommentVNode("", true)
          ], 32),
          _createElementVNode("div", {
            class: "timeline-row select-row",
            onContextmenu: _cache[10] || (_cache[10] = _withModifiers(($event: any) => (openTrackContextMenu($event, { kind: 'select' })), ["prevent","stop"]))
          }, [
            _cache[26] || (_cache[26] = _createElementVNode("div", { class: "line-label select-label" }, [
              _createElementVNode("strong", null, "选项卡"),
              _createElementVNode("small", null, "可选 · 固定单 Clip")
            ], -1)),
            (__props.node.select)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  "data-timeline-clip": "",
                  class: _normalizeClass(["timeline-clip select-clip resizable-clip", { selected: selectedId.value === __props.node.select.id }]),
                  style: _normalizeStyle({
                left: `${labelWidth + __props.node.select.startTime * pixelsPerSecond.value}px`,
              }),
                  onPointerdown: _cache[6] || (_cache[6] = ($event: any) => (startDrag($event, __props.node.select))),
                  onContextmenu: _cache[7] || (_cache[7] = _withModifiers(($event: any) => (openClipContextMenu($event, { kind: 'select', clip: __props.node.select })), ["prevent","stop"])),
                  onPointerenter: _cache[8] || (_cache[8] = ($event: any) => (
                showPreview($event, { kind: 'select', clip: __props.node.select })
              )),
                  onPointermove: movePreview,
                  onPointerleave: hidePreview,
                  onClick: _cache[9] || (_cache[9] = _withModifiers(($event: any) => (
                openEditor($event, { kind: 'select', clip: __props.node.select })
              ), ["stop"]))
                }, [
                  _createElementVNode("i", {
                    class: "clip-resize-handle resize-start",
                    title: "拖动设置开始时间",
                    onPointerdown: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (startResize($event, __props.node.select, 'start')), ["stop"]))
                  }, null, 32),
                  _createElementVNode("span", null, _toDisplayString(__props.node.select.options.length) + " 个选项", 1),
                  _createElementVNode("small", null, _toDisplayString(__props.node.select.startTime.toFixed(1)) + "s → 时间轴末尾 ", 1),
                  _createElementVNode("i", {
                    class: "continue-delay-handle",
                    style: _normalizeStyle({
                  left: `${__props.node.select.continueDelayTime * pixelsPerSecond.value}px`,
                }),
                    title: "拖动设置 ContinueDelayTime",
                    onPointerdown: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (startContinueDelayDrag($event, __props.node.select)), ["stop"]))
                  }, [
                    _createElementVNode("em", null, _toDisplayString(__props.node.select.continueDelayTime.toFixed(1)) + "s", 1)
                  ], 36)
                ], 38))
              : (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "empty-line",
                  style: _normalizeStyle({ left: `${labelWidth + 16}px` }),
                  onClick: addSelectClip
                }, " ＋ 添加 选项卡片段 ", 4))
          ], 32),
          _createElementVNode("div", {
            class: "timeline-row focus-push-row",
            onContextmenu: _cache[15] || (_cache[15] = _withModifiers(($event: any) => (openTrackContextMenu($event, { kind: 'focusPush' })), ["prevent","stop"]))
          }, [
            _cache[28] || (_cache[28] = _createElementVNode("div", { class: "line-label" }, [
              _createElementVNode("div", null, [
                _createElementVNode("strong", null, "强制跳过"),
                _createElementVNode("small", null, "强制跳过 · 单 Clip")
              ])
            ], -1)),
            (__props.node.focusPush)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  "data-timeline-clip": "",
                  class: _normalizeClass(["timeline-clip focus-push-clip", { selected: selectedId.value === __props.node.focusPush.id }]),
                  style: _normalizeStyle({ left: `${labelWidth + __props.node.focusPush.startTime * pixelsPerSecond.value}px` }),
                  onPointerdown: _cache[11] || (_cache[11] = ($event: any) => (startDrag($event, __props.node.focusPush))),
                  onContextmenu: _cache[12] || (_cache[12] = _withModifiers(($event: any) => (openClipContextMenu($event, { kind: 'focusPush', clip: __props.node.focusPush })), ["prevent","stop"])),
                  onPointerenter: _cache[13] || (_cache[13] = ($event: any) => (showPreview($event, { kind: 'focusPush', clip: __props.node.focusPush }))),
                  onPointermove: movePreview,
                  onPointerleave: hidePreview,
                  onClick: _cache[14] || (_cache[14] = _withModifiers(($event: any) => (openEditor($event, { kind: 'focusPush', clip: __props.node.focusPush })), ["stop"]))
                }, [
                  _cache[27] || (_cache[27] = _createElementVNode("span", null, "◆ 强制跳过", -1)),
                  _createElementVNode("small", null, _toDisplayString(__props.node.focusPush.startTime.toFixed(1)) + "s", 1)
                ], 38))
              : (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "empty-line",
                  style: _normalizeStyle({ left: `${labelWidth + 16}px` }),
                  onClick: addFocusPushClip
                }, " ＋ 添加 强制跳过片段 ", 4))
          ], 32),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.node.lines, (line) => {
            return (_openBlock(), _createElementBlock("div", {
              key: line.id,
              class: "timeline-row",
              onContextmenu: _withModifiers(($event: any) => (openTrackContextMenu($event, { kind: 'performance', lineId: line.id })), ["prevent","stop"])
            }, [
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("div", null, [
                  _createElementVNode("strong", null, _toDisplayString(line.name === line.type ? lineLabel(line) : line.name), 1),
                  _createElementVNode("small", null, _toDisplayString(lineLabel(line)) + "片段", 1)
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("button", {
                    type: "button",
                    "aria-label": `在 ${line.name} 添加 Clip`,
                    onClick: ($event: any) => (addPerformanceClip(line))
                  }, " ＋ ", 8, _hoisted_22),
                  (_unref(getLineDefinition)(line.type)?.removable !== false)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        "aria-label": `删除 ${line.name} Line`,
                        onClick: ($event: any) => (deleteLine(line))
                      }, " × ", 8, _hoisted_23))
                    : _createCommentVNode("", true)
                ])
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(line.clips, (clip) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: clip.id,
                  type: "button",
                  "data-timeline-clip": "",
                  class: _normalizeClass(["timeline-clip performance-clip resizable-clip", [
                `clip-${line.type.toLowerCase()}`,
                { selected: selectedId.value === clip.id, 'auto-width-clip': !_unref(isPerformanceClipResizable)(clip) },
              ]]),
                  style: _normalizeStyle({
                left: `${labelWidth + clip.startTime * pixelsPerSecond.value}px`,
                width: _unref(isPerformanceClipResizable)(clip) ? `${Math.max(_unref(getPerformanceClipDuration)(clip), 0.1) * pixelsPerSecond.value}px` : undefined,
              }),
                  onPointerdown: ($event: any) => (startDrag($event, clip)),
                  onContextmenu: _withModifiers(($event: any) => (openClipContextMenu($event, { kind: 'performance', line, clip })), ["prevent","stop"]),
                  onPointerenter: ($event: any) => (
                showPreview($event, { kind: 'performance', line, clip })
              ),
                  onPointermove: movePreview,
                  onPointerleave: hidePreview,
                  onClick: _withModifiers(($event: any) => (
                openEditor($event, { kind: 'performance', line, clip })
              ), ["stop"])
                }, [
                  (_unref(isPerformanceClipResizable)(clip))
                    ? (_openBlock(), _createElementBlock("i", {
                        key: 0,
                        class: "clip-resize-handle resize-start",
                        title: "拖动设置开始时间",
                        onPointerdown: _withModifiers(($event: any) => (startResize($event, clip, 'start')), ["stop"])
                      }, null, 40, _hoisted_25))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    title: performanceClipLabel(clip)
                  }, _toDisplayString(performanceClipLabel(clip)), 9, _hoisted_26),
                  _createElementVNode("small", null, [
                    _createTextVNode(_toDisplayString(clip.startTime.toFixed(1)) + "s ", 1),
                    (_unref(isPerformanceClipResizable)(clip))
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(" + " + _toDisplayString(_unref(getPerformanceClipDuration)(clip).toFixed(1)) + "s", 1)
                        ], 64))
                      : _createCommentVNode("", true)
                  ]),
                  (_unref(isPerformanceClipResizable)(clip))
                    ? (_openBlock(), _createElementBlock("i", {
                        key: 1,
                        class: "clip-resize-handle resize-end",
                        title: "拖动设置持续时间",
                        onPointerdown: _withModifiers(($event: any) => (startResize($event, clip, 'end')), ["stop"])
                      }, null, 40, _hoisted_27))
                    : _createCommentVNode("", true)
                ], 46, _hoisted_24))
              }), 128)),
              (!line.clips.length)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "empty-line",
                    style: _normalizeStyle({ left: `${labelWidth + 16}px` }),
                    onClick: ($event: any) => (addPerformanceClip(line))
                  }, " ＋ 添加 " + _toDisplayString(lineLabel(line)) + "片段 ", 13, _hoisted_28))
                : _createCommentVNode("", true)
            ], 40, _hoisted_19))
          }), 128)),
          _createElementVNode("div", _hoisted_29, [
            _createElementVNode("button", {
              type: "button",
              class: "add-event-button",
              "aria-haspopup": "menu",
              "aria-expanded": !!footerLineMenu.value,
              onClick: openFooterLineMenu
            }, "＋ 添加事件", 8, _hoisted_30)
          ])
        ], 4)
      ], 512)
    ]),
    (footerLineMenu.value)
      ? (_openBlock(), _createBlock(TimelineContextMenu, {
          key: 1,
          position: { x: footerLineMenu.value.x, y: footerLineMenu.value.y },
          title: "添加事件",
          items: _unref(lineDefinitions).map(definition => ({ id: definition.type, label: `添加${definition.label}` })),
          onSelect: addLine,
          onClose: closeFooterLineMenu
        }, null, 8, ["position", "items"]))
      : _createCommentVNode("", true),
    (timelineContextMenu.value)
      ? (_openBlock(), _createBlock(TimelineContextMenu, {
          key: 2,
          position: { x: timelineContextMenu.value.x, y: timelineContextMenu.value.y },
          title: timelineContextMenu.value.clipId ? 'Clip 操作' : `轨道 · ${timelineContextMenu.value.time.toFixed(1)}s`,
          items: contextMenuItems.value,
          onSelect: timelineContextAction,
          onClose: closeTimelineContextMenu
        }, null, 8, ["position", "title", "items"]))
      : _createCommentVNode("", true),
    (hoveredClip.value && !editorOpen.value && !timelineContextMenu.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 3,
          class: "clip-preview",
          style: _normalizeStyle({
        left: `${previewPosition.value.left}px`,
        top: `${previewPosition.value.top}px`,
      })
        }, [
          _createElementVNode("strong", null, _toDisplayString(hoveredClip.value.kind === "dialogue"
            ? "对话片段"
            : hoveredClip.value.kind === "select"
              ? "选项卡片段"
              : hoveredClip.value.kind === "focusPush" ? "强制跳过片段" : `${lineLabel(hoveredClip.value.line)}片段`), 1),
          (hoveredClip.value.kind === 'dialogue')
            ? (_openBlock(), _createElementBlock("span", _hoisted_31, _toDisplayString(hoveredClip.value.clip.speaker || "未设置说话人") + "：" + _toDisplayString(hoveredClip.value.clip.content || "未填写台词"), 1))
            : (hoveredClip.value.kind === 'select')
              ? (_openBlock(), _createElementBlock("span", _hoisted_32, _toDisplayString(hoveredClip.value.clip.options.length) + " 个选项 · " + _toDisplayString(hoveredClip.value.clip.style), 1))
              : (hoveredClip.value.kind === 'focusPush')
                ? (_openBlock(), _createElementBlock("span", _hoisted_33, "时间到达后强制播放连接的下一句话"))
                : (_openBlock(), _createElementBlock("span", _hoisted_34, _toDisplayString(performanceClipLabel(hoveredClip.value.clip)), 1)),
          (hoveredClip.value.kind === 'performance' && hoveredClip.value.clip.type === 'Camera')
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 4 }, _renderList(_unref(getCameraClipPreview)(hoveredClip.value.clip), (summary, index) => {
                return (_openBlock(), _createElementBlock("span", { key: index }, _toDisplayString(summary), 1))
              }), 128))
            : _createCommentVNode("", true),
          _createElementVNode("small", null, [
            _createTextVNode(" 开始 " + _toDisplayString(hoveredClip.value.clip.startTime.toFixed(1)) + "s ", 1),
            (clipDisplayDuration(hoveredClip.value.clip) > 0)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" · 持续 " + _toDisplayString(clipDisplayDuration(hoveredClip.value.clip).toFixed(1)) + "s ", 1)
                ], 64))
              : _createCommentVNode("", true)
          ])
        ], 4))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, {
      to: __props.inspectorTarget || 'body'
    }, [
      (editorOpen.value && selectedClip.value)
        ? (_openBlock(), _createElementBlock("aside", {
            key: 0,
            "data-clip-editor": "",
            class: _normalizeClass(["clip-editor-popover dsfg-typography", { 'camera-popover': selectedClip.value.kind === 'performance' && selectedClip.value.clip.type === 'Camera', 'clip-editor-docked': __props.inspectorTarget }]),
            "aria-label": "Clip 参数"
          }, [
            _createElementVNode("header", _hoisted_35, [
              _createElementVNode("span", null, _toDisplayString(selectedClip.value.kind === "dialogue"
                ? "对话片段"
                : selectedClip.value.kind === "select"
                  ? "选项卡片段"
                  : selectedClip.value.kind === "focusPush" ? "强制跳过片段" : selectedClip.value.clip.type === 'Camera' ? '相机设置' : `${lineLabel(selectedClip.value.line)}片段`), 1),
              _createElementVNode("button", {
                type: "button",
                "aria-label": "关闭 Clip 参数",
                onClick: _cache[16] || (_cache[16] = ($event: any) => (editorOpen.value = false))
              }, " × ")
            ]),
            (_openBlock(), _createElementBlock("div", {
              key: selectedClip.value.clip.id,
              class: "popover-content"
            }, [
              (selectedClip.value.kind === 'dialogue')
                ? (_openBlock(), _createBlock(DialogueClipEditor, {
                    key: 0,
                    clip: selectedClip.value.clip,
                    duration: _unref(getFlowClipDuration)(__props.node, selectedClip.value.clip)
                  }, null, 8, ["clip", "duration"]))
                : (selectedClip.value.kind === 'select')
                  ? (_openBlock(), _createBlock(SelectClipEditor, {
                      key: 1,
                      clip: selectedClip.value.clip
                    }, null, 8, ["clip"]))
                  : (selectedClip.value.kind === 'focusPush')
                    ? (_openBlock(), _createBlock(FocusPushClipEditor, {
                        key: 2,
                        clip: selectedClip.value.clip,
                        node: __props.node
                      }, null, 8, ["clip", "node"]))
                    : (_openBlock(), _createBlock(PerformanceClipEditor, {
                        key: 3,
                        clip: selectedClip.value.clip
                      }, null, 8, ["clip"])),
              (selectedClip.value.kind !== 'focusPush')
                ? (_openBlock(), _createElementBlock("div", _hoisted_36, [
                    _createElementVNode("label", null, [
                      _cache[29] || (_cache[29] = _createTextVNode(" 开始时间 ", -1)),
                      _createElementVNode("input", {
                        type: "number",
                        min: "0",
                        step: "0.1",
                        value: selectedClip.value.clip.startTime,
                        onInput: _cache[17] || (_cache[17] = ($event: any) => (updateStartTime(selectedClip.value.clip, $event)))
                      }, null, 40, _hoisted_37)
                    ]),
                    (selectedClip.value.kind === 'performance' && _unref(isPerformanceClipResizable)(selectedClip.value.clip))
                      ? (_openBlock(), _createElementBlock("label", _hoisted_38, [
                          _cache[30] || (_cache[30] = _createTextVNode(" 持续时间 ", -1)),
                          _createElementVNode("input", {
                            type: "number",
                            step: "0.1",
                            value: selectedClip.value.clip.duration,
                            min: selectedClip.value.clip.type === 'PublicEvent' ? 0 : _unref(MIN_CLIP_DURATION),
                            onInput: _cache[18] || (_cache[18] = ($event: any) => (updatePerformanceDuration(selectedClip.value.clip, $event)))
                          }, null, 40, _hoisted_39)
                        ]))
                      : (selectedClip.value.kind !== 'performance')
                        ? (_openBlock(), _createElementBlock("label", _hoisted_40, [
                            _cache[31] || (_cache[31] = _createTextVNode(" ContinueDelayTime ", -1)),
                            _createElementVNode("input", {
                              type: "number",
                              min: "0",
                              step: "0.1",
                              value: selectedClip.value.clip.continueDelayTime,
                              onInput: _cache[19] || (_cache[19] = ($event: any) => (updateContinueDelay(selectedClip.value.clip, $event)))
                            }, null, 40, _hoisted_41)
                          ]))
                        : _createCommentVNode("", true),
                    (selectedClip.value.kind === 'dialogue' && _unref(isFreeDialogueClip)(selectedClip.value.clip))
                      ? (_openBlock(), _createElementBlock("label", _hoisted_42, [
                          _cache[32] || (_cache[32] = _createTextVNode(" 持续时间 ", -1)),
                          _createElementVNode("input", {
                            type: "number",
                            min: _unref(MIN_CLIP_DURATION),
                            step: "0.1",
                            value: _unref(getFlowClipDuration)(__props.node, selectedClip.value.clip),
                            onInput: _cache[20] || (_cache[20] = ($event: any) => (updateDialogueDuration(selectedClip.value.clip, $event)))
                          }, null, 40, _hoisted_43)
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]))
          ], 2))
        : _createCommentVNode("", true)
    ], 8, ["to"]))
  ], 512))
}
}

})